/**
 * Cloud bridge — sends files to the Scrivar processing API for server-side operations.
 * Used by the web app when native desktop tools aren't available.
 * Mirrors the nativebridge.js API so pages can swap seamlessly.
 */

import { getIdToken } from './auth';

const PROCESSING_URL = import.meta.env.VITE_PROCESSING_URL || 'https://process.scrivar.com';

// 2 minutes covers large LibreOffice conversions; anything longer is almost
// certainly a stuck backend and the user should see an error instead of a
// spinner that never resolves.
const PROCESSING_TIMEOUT_MS = 120000;

async function processFile(endpoint, file, params = {}, opts = {}) {
  // opts.allowAnonymous lets a caller (compress) send without a token; the
  // server gates anonymous access + rate-limits it. Other ops keep requiring
  // sign-in (they 401 anonymously) so this stays a no-op for them.
  const token = await getIdToken().catch(() => null);
  if (!token && !opts.allowAnonymous) {
    throw new Error('You must be signed in to use cloud processing.');
  }

  const formData = new FormData();

  // file can be ArrayBuffer, Uint8Array, Blob, or File
  if (file instanceof ArrayBuffer || file instanceof Uint8Array) {
    formData.append('file', new Blob([file], { type: 'application/octet-stream' }), 'input.pdf');
  } else {
    formData.append('file', file);
  }

  // Add additional params
  for (const [key, value] of Object.entries(params)) {
    formData.append(key, value);
  }

  const ac = new AbortController();
  const timeout = setTimeout(() => ac.abort(), PROCESSING_TIMEOUT_MS);
  let res;
  try {
    res = await fetch(`${PROCESSING_URL}/${endpoint}`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
      signal: ac.signal,
    });
  } catch (err) {
    clearTimeout(timeout);
    if (err?.name === 'AbortError') {
      throw new Error('Processing is taking too long. Please try again with a smaller file.');
    }
    throw err;
  }
  clearTimeout(timeout);

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Processing failed' }));
    // Attach status + code so callers (classifyCloudError) can distinguish a
    // 429 rate-limit from auth/network/server failures without regex.
    const e = new Error(body.error || `Server error ${res.status}`);
    e.status = res.status;
    e.code = body.code || null;
    throw e;
  }

  // Optional per-call hook for surfacing response headers (e.g. compression
  // strategy / reduction / classification from the orchestrator). Callback
  // approach keeps the function's primary return shape (ArrayBuffer)
  // unchanged for the rest of the call sites that don't care about meta.
  if (typeof opts.onMeta === 'function') {
    try {
      opts.onMeta(res.headers);
    } catch {
      // Never let a UI-side handler error abort the data path.
    }
  }

  return await res.arrayBuffer();
}

/**
 * Compress a PDF via the processing API.
 *
 * @param {ArrayBuffer|Uint8Array} pdfBytes
 * @param {'low'|'medium'|'high'} level
 * @param {Object} [opts]
 * @param {(headers: Headers) => void} [opts.onMeta]
 *   Optional callback invoked with the response Headers object so callers
 *   can read X-Scrivar-Compression-* without changing the primary return
 *   shape. Errors thrown by the callback are swallowed — UI bugs must not
 *   defeat the compression result.
 * @returns {Promise<ArrayBuffer>}
 */
export async function compressPDF(pdfBytes, level = 'medium', opts = {}) {
  // Compress is the one op anonymous web visitors may run in the cloud.
  return processFile('compress', pdfBytes, { level }, { ...opts, allowAnonymous: true });
}

export async function convertFile(fileBytes, inputExt, outputFormat) {
  // Create a blob with the correct filename so the server knows the input format
  const blob = new Blob([fileBytes], { type: 'application/octet-stream' });
  const file = new File([blob], `input${inputExt}`);
  return processFile('convert', file, { outputFormat });
}

export async function ocrImage(imageBytes, language = 'eng') {
  return processFile('ocr', imageBytes, { language });
}

export async function unlockPDF(pdfBytes, password) {
  return processFile('unlock', pdfBytes, { password });
}

export async function protectPDF(pdfBytes, { password, ownerPassword = '', permissions = null } = {}) {
  const params = { password };
  if (ownerPassword) params.ownerPassword = ownerPassword;
  if (permissions) params.permissions = JSON.stringify(permissions);
  return processFile('protect', pdfBytes, params);
}

export async function flattenPDF(pdfBytes) {
  return processFile('flatten', pdfBytes);
}
